import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  ActionCountsByStatus,
  ActionsBySourceCategory,
  calculateActionProgressBySourceType,
  calculateProjectBudgetPercentageType,
  CountActionsBySCFResponse,
  countSecurityCheckFamilyByProjectType,
  CriticalRatio,
  getActionsBudgetTableType,
  groupActionsBySecurityCheckFamilyType,
  Percentage,
  Projet,
  projetOverdueProgressCritical,
  ProjetPriority,
  ProjetRes,
  ProjetRes1,
  ProjetResponse,
  ProjetType,
} from '../../types/projetTypes'
import {
  getAllProjets,
  addProjetApi,
  deleteProjetApi,
  getProjetbyIdApi,
  updateProjetApi,
  getProjetbyPipelineApi,
  getProjetbyMissionApi,
  removeProjetFromPipelineApi,
  addProjetToPipelineApi,
  countActionsStatusByProjetIdApi,
  getActionStatusCoursRatioApi,
  getActionStatusDoneRatioApi,
  getOverdueInProgressActionRatioApi,
  countActionsPriorityByProjetIdApi,
  getActionPriorityMediumRatioApi,
  getActionPriorityLowRatioApi,
  getOverdueInProgressCriticalHighActionRatioApi,
  countActionsBySourceCategoryApi,
  actionCountsByStatusApi,
  actionCountsByTypeAndStatusApi,
  calculateProjectBudgetPercentageApi,
  getActionsBudgetTableApi,
  calculateActionProgressBySourceApi,
  groupActionsBySecurityCheckFamilyApi,
  countSecurityCheckFamilyByProjectApi,
  countActionsBySCFForProject,
} from '../../api/projetApi'
import { parseProjetFloatFields } from '../../utils/parseFloatFields'
import { handleError } from '../../utils/errorHandler'

export const getProjets = createAsyncThunk<
  {
    projets?: Projet[]
    total?: number
    totalPages?: number
    status?: number
    erreur?: boolean
    message?: string
  },
  { search?: string; page?: number; pageSize?: number },
  { rejectValue: string }
>('projet/getAllProjets', async ({ search, page, pageSize }, thunkAPI) => {
  try {
    const response = await getAllProjets({ search, page, pageSize })
    return {
      projets: response.projets,
      total: response.total,
      totalPages: response.totalPages,
      status: response.status,
      message: response.message,
      erreur: response.erreur,
    }
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const deleteProjet = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>('projet/deleteProjet', async (ProjetId, thunkAPI) => {
  try {
    await deleteProjetApi(ProjetId)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const addProjet = createAsyncThunk<
  Projet,
  Projet,
  { rejectValue: string }
>('projet/addProjet', async (newProjet, thunkAPI) => {
  try {
    // Parse float fields before sending to API
    const parsedProjet = parseProjetFloatFields(newProjet)
    const response = await addProjetApi(parsedProjet)
    console.log(response)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
export const getProjetbyId = createAsyncThunk<
  Projet,
  string,
  { rejectValue: string }
>('projet/getProjetbyId', async (ProjetId, thunkAPI) => {
  try {
    const response = await getProjetbyIdApi(ProjetId)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const updateProjet = createAsyncThunk<
  Projet,
  { updatedProjet: Projet; _id: string },
  { rejectValue: string }
>('projet/updateProjet', async ({ updatedProjet, _id }, thunkAPI) => {
  try {
    // Parse float fields before sending to API
    const parsedProjet = parseProjetFloatFields(updatedProjet)
    const response = await updateProjetApi(parsedProjet, _id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
export const getProjetbyPipeline = createAsyncThunk<
  ProjetRes1,
  string,
  { rejectValue: string }
>('projet/getProjetbyPipeline', async (PipelineId, thunkAPI) => {
  try {
    const response = await getProjetbyPipelineApi(PipelineId)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getProjetbyMission = createAsyncThunk<
  { projets?: Projet[]; total?: number; totalPages?: number },
  { page?: number; pageSize?: number; MissionId: string },
  { rejectValue: string }
>(
  'projet/getProjetbyMissionId',
  async ({ page, pageSize, MissionId }, thunkAPI) => {
    try {
      const response = await getProjetbyMissionApi(MissionId, page, pageSize)
      return {
        projets: response.projets,
        total: response.total,
        totalPages: response.totalPages,
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const addProjetsToPipeline = createAsyncThunk<
  Projet[],
  { ProjetId: string[]; _id: string },
  { rejectValue: string }
>('projet/addProjetsToPipeline', async ({ ProjetId, _id }, thunkAPI) => {
  try {
    const response = await addProjetToPipelineApi(ProjetId, _id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
export const removeProjetFromPipeline = createAsyncThunk<
  Projet[],
  { projets: ProjetType; _id: string },
  { rejectValue: string }
>('projet/RemoveProjetFromPipeline', async ({ projets, _id }, thunkAPI) => {
  try {
    const response = await removeProjetFromPipelineApi(projets, _id)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const countActionsStatusByProjetId = createAsyncThunk<
  ProjetResponse,
  { idProjet: string; status?: string },
  { rejectValue: string }
>('projets/ActionsStatusByProjetId', async ({ idProjet, status }, thunkAPI) => {
  try {
    const response = await countActionsStatusByProjetIdApi(idProjet, status)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionStatusCoursRatio = createAsyncThunk<
  ProjetResponse,
  { idProjet: string },
  { rejectValue: string }
>('projets/ActionStatusCoursRatio', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await getActionStatusCoursRatioApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionStatusDoneRatio = createAsyncThunk<
  ProjetResponse,
  { idProjet: string },
  { rejectValue: string }
>('projets/actionStatusDoneRatio', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await getActionStatusDoneRatioApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getOverdueInProgressActionRatio = createAsyncThunk<
  ProjetResponse,
  { idProjet: string },
  { rejectValue: string }
>('projets/overdueInProgressActionRatio', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await getOverdueInProgressActionRatioApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const countActionsPriorityByProjetId = createAsyncThunk<
  ProjetPriority,
  { idProjet: string; priority?: number },
  { rejectValue: string }
>(
  'projets/countActionsStatusByProjetId',
  async ({ idProjet, priority }, thunkAPI) => {
    try {
      const response = await countActionsPriorityByProjetIdApi(
        idProjet,
        priority
      )
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const getActionPriorityCriticalRatio = createAsyncThunk<
  CriticalRatio,
  { idProjet: string },
  { rejectValue: string }
>('projets/actionPriorityCriticalRatio', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await getOverdueInProgressActionRatioApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionPriorityHighRatio = createAsyncThunk<
  ProjetResponse,
  { idProjet: string },
  { rejectValue: string }
>('projets/getActionPriorityHighRatio', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await getOverdueInProgressActionRatioApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionPriorityMediumRatio = createAsyncThunk<
  ProjetResponse,
  { idProjet: string },
  { rejectValue: string }
>('projets/getActionPriorityMediumRatio', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await getActionPriorityMediumRatioApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getActionPriorityLowRatio = createAsyncThunk<
  ProjetResponse,
  { idProjet: string },
  { rejectValue: string }
>('projets/getActionPriorityLowRatio', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await getActionPriorityLowRatioApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getOverdueInProgressCriticalHighActionRatio = createAsyncThunk<
  projetOverdueProgressCritical,
  { idProjet: string },
  { rejectValue: string }
>(
  'projets/overdueInProgressCriticalHighActionRatio',
  async ({ idProjet }, thunkAPI) => {
    try {
      const response =
        await getOverdueInProgressCriticalHighActionRatioApi(idProjet)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const getActionCountsBySourceCategory = createAsyncThunk<
  ActionsBySourceCategory,
  { idProjet: string },
  { rejectValue: string }
>('projets/getActionCountsBySourceCategory', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await countActionsBySourceCategoryApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getactionCountsByStatus = createAsyncThunk<
  ActionCountsByStatus,
  { idProjet: string },
  { rejectValue: string }
>('projets/getactionCountsByStatus', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await actionCountsByStatusApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const getactionCountsByTypeAndStatus = createAsyncThunk<
  ActionCountsByStatus,
  { idProjet: string },
  { rejectValue: string }
>('projets/getactionCountsByTypeAndStatus', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await actionCountsByTypeAndStatusApi(idProjet)
    console.log('ziedddd' + JSON.stringify(response))
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const CalculateProjectBudgetPercentage = createAsyncThunk<
  calculateProjectBudgetPercentageType,
  { idProjet: string },
  { rejectValue: string }
>(
  'projets/calculateProjectBudgetPercentage',
  async ({ idProjet }, thunkAPI) => {
    try {
      const response = await calculateProjectBudgetPercentageApi(idProjet)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const GetActionsBudgetTable = createAsyncThunk<
  getActionsBudgetTableType,
  { idProjet: string },
  { rejectValue: string }
>('projets/getActionsBudgetTable', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await getActionsBudgetTableApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const CalculateActionProgressBySource = createAsyncThunk<
  calculateActionProgressBySourceType,
  { idProjet: string },
  { rejectValue: string }
>('projets/CalculateActionProgressBySource', async ({ idProjet }, thunkAPI) => {
  try {
    const response = await calculateActionProgressBySourceApi(idProjet)
    return response
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})

export const GroupActionsBySecurityCheckFamily = createAsyncThunk<
  groupActionsBySecurityCheckFamilyType,
  { idProjet: string },
  { rejectValue: string }
>(
  'projets/GroupActionsBySecurityCheckFamily',
  async ({ idProjet }, thunkAPI) => {
    try {
      const response = await groupActionsBySecurityCheckFamilyApi(idProjet)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)

export const CountSecurityCheckFamilyByProject = createAsyncThunk<
  countSecurityCheckFamilyByProjectType,
  { idProjet: string },
  { rejectValue: string }
>(
  'projets/CountSecurityCheckFamilyByProject',
  async ({ idProjet }, thunkAPI) => {
    try {
      const response = await countSecurityCheckFamilyByProjectApi(idProjet)
      return response
    } catch (error) {
      return thunkAPI.rejectWithValue(await handleError(error))
    }
  }
)
export const fetchSCFCounts = createAsyncThunk<
  CountActionsBySCFResponse,
  string,
  { rejectValue: string }
>('project/fetchSCFCounts', async (projectId, thunkAPI) => {
  try {
    return await countActionsBySCFForProject(projectId)
  } catch (error) {
    return thunkAPI.rejectWithValue(await handleError(error))
  }
})
